import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';
import { leaveSignedOut } from 'utils/signedOut';
import { BasedOn, refusedOrVersion, withBasedOn } from 'api/keptVersion';

/**
 * A mail template stores itself once it exists (auto-save, scene 16): no
 * template exists until "Create" is pressed; from then on it keeps itself
 * like everything else. It is one of the four settings screens on which
 * NOTHING is stored while a required field is empty (scene 21, D34, D35).
 */
export interface TemplateWork {
  name: string;
  language_id: number;
  subject: string;
  body: string;
}

const hasWords = (html: string): boolean =>
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim() !== '';

/** What is still missing, in the words the corner and the leave question use. */
export const missingForTemplate = (work: TemplateWork): string[] => [
  ...(work.name.trim() === '' ? ['the template needs a name'] : []),
  ...(!work.language_id ? ['a language is required'] : []),
  ...(work.subject.trim() === '' ? ['a subject is required'] : []),
  ...(!hasWords(work.body) ? ['the mail needs a text'] : []),
];

export const templateBody = (
  work: TemplateWork,
  id?: number | string
): Record<string, unknown> => ({
  ...(id !== undefined ? { id } : {}),
  name: work.name,
  language_id: work.language_id,
  subject: work.subject,
  body: work.body,
});

export const TEMPLATES_PATH = '/v4/templates';

/**
 * Stores the template (with a number: that one; without: a new one) and
 * answers its number. @throws when the server did not confirm
 */
export const saveTemplate = async (
  work: TemplateWork,
  id?: number | string,
  signal?: AbortSignal,
  basedOn?: BasedOn,
  stored?: (version: number | null) => void
): Promise<number> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';
  const response = await fetch(`${base}${TEMPLATES_PATH}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
      'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
    },
    body: JSON.stringify(withBasedOn(templateBody(work, id), basedOn)),
    signal,
  });
  if (response.status === 401) {
    if (window.location.pathname !== '/') leaveSignedOut();
    throw new Error('The session has ended.');
  }
  // D10: out of date is not a failure; the person chooses.
  const version = await refusedOrVersion(response);
  if (!response.ok) {
    throw new Error(`The template was not stored (${response.status}).`);
  }
  const answer = (await response.json().catch(() => ({}))) as {
    success?: boolean;
    data?: { id?: number };
  };
  if (answer.success !== true || typeof answer.data?.id !== 'number') {
    throw new Error('The template was not stored.');
  }
  stored?.(version);
  return answer.data.id;
};
